/*───────────────────────────────────────────────────────────────
 🌐 Project: Ring2All Platform
 🧑‍💻 Author: Rodrigo Cuadra
 🏢 Company: Ring2All LLC
 📄 File: PickupGroupsFormView.tsx
 🗓️ Created: 2025-01-15
 🧩 Description:
    Form view for creating and editing Pickup Groups - simplified using hooks.
────────────────────────────────────────────────────────────────*/

import { useState, useEffect, useMemo, useCallback } from 'react';
import { useNavigate, useParams, useSearchParams } from 'react-router-dom';
import { PhoneOff, Search, Copy, Plus } from 'lucide-react';
import { useI18n } from '../../hooks/useI18n';
import { useNotifications } from '../../contexts/NotificationContext';
import FixedActionBar from '../../components/ui/FixedActionBar';
import ConfirmationModal from '../../components/ui/ConfirmationModal';
import { ModuleFormHeader } from '../../components/layouts';
import { useSaveWithAction } from '../../hooks/useSaveWithAction';
import { deletePickupGroupById, navigateToPickupGroupDuplicate } from './pickupGroupsActions';
import { QuickSearchModal } from '../../components/ui/QuickSearchModal';
import { usePickupGroupData } from './hooks/usePickupGroupData';
import { usePickupGroupForm } from './hooks/usePickupGroupForm';
import { fetchPickupGroups } from './api/pickupGroupsApi';
import { DEFAULT_SETTINGS } from './utils/formDefaults';
import type { PickupGroupMember } from './types';
import { PickupGroupGeneralBox } from './components/PickupGroupGeneralBox';
import { PickupGroupMembersBox } from './components/PickupGroupMembersBox';

export default function PickupGroupsFormView() {
  const { id } = useParams<{ id: string }>();
  const [searchParams] = useSearchParams();
  const duplicateId = searchParams.get('duplicate');
  const isEditMode = Boolean(id && id !== 'new');
  const navigate = useNavigate();
  const { t } = useI18n();
  const { showSuccess, showError } = useNotifications();
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [isDeleting, setIsDeleting] = useState(false);

  // Use hooks for data and form management
  const {
    formData,
    setFormData,
    members,
    setMembers,
    availableExtensions,
    isLoading,
    loadPickupGroup
  } = usePickupGroupData({
    id,
    duplicateId,
    isEditMode
  });

  // Store original data for change detection
  const [originalFormData, setOriginalFormData] = useState(formData);
  const [originalMembers, setOriginalMembers] = useState<PickupGroupMember[]>([]);

  // Update original data after loading completes
  useEffect(() => {
    if (isEditMode && !isLoading && formData.name) {
      setOriginalFormData({ ...formData });
      setOriginalMembers([...members]);
    }
  }, [isLoading, isEditMode, formData, members]);

  // Use form hook for validation and submission
  const {
    errors,
    setErrors,
    isSaving,
    hasChanges,
    handleSave: handleSaveForm,
    handleCancel: handleCancelForm
  } = usePickupGroupForm({
    formData,
    setFormData,
    members,
    setMembers,
    originalFormData,
    originalMembers,
    isEditMode,
    pickupGroupId: id,
    onLoadPickupGroup: loadPickupGroup
  });

  // Enhanced save handler
  const handleSaveInternal = useCallback(async () => {
    const savedData = await handleSaveForm();

    // Update original data after successful save
    if (savedData) {
      setOriginalFormData({ ...formData });
      setOriginalMembers([...members]);
    }

    // If no changes were detected, return current data to allow navigation
    if (!savedData && isEditMode && id) {
      return { id, ...formData };
    }

    return savedData;
  }, [handleSaveForm, formData, members, isEditMode, id]);

  // Use reusable hook for save actions
  const mode = isEditMode ? 'edit' : 'create';
  const handleSaveWithAction = useSaveWithAction({
    onSave: handleSaveInternal,
    listPath: '/pbx/applications/pickup-groups',
    newPath: '/pbx/applications/pickup-groups/new',
    mode,
    getEditPath: (savedData) => `/pbx/applications/pickup-groups/${savedData.id || savedData.uuid}`,
    getFormData: () => formData,
    storageKey: 'pickupGroups:saveAndNewData',
  });

  // Quick List (Pickup Groups) state
  const [showQuickList, setShowQuickList] = useState(false);
  const [quickListSearch, setQuickListSearch] = useState('');
  const [quickListGroups, setQuickListGroups] = useState<any[]>([]);
  const [initialGroups, setInitialGroups] = useState<any[]>([]);
  const [isLoadingInitial, setIsLoadingInitial] = useState(false);
  const [isQuickSearching, setIsQuickSearching] = useState(false);

  // Field change handler
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  const handleChange = useCallback((field: string, value: any) => {
    setFormData(prev => ({ ...prev, [field]: value }));
    // Clear error when field is modified
    if (errors[field]) {
      setErrors(prev => {
        const updated = { ...prev };
        delete updated[field];
        return updated;
      });
    }
  }, [setFormData, errors, setErrors]);

  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  const handleSettingChange = useCallback((key: string, value: any) => {
    setFormData(prev => ({
      ...prev,
      settings: {
        ...prev.settings,
        [key]: value
      }
    }));
  }, [setFormData]);

  const handleMemberChange = useCallback(
    (index: number, field: keyof PickupGroupMember, value: string | boolean) => {
      setMembers(prev => {
        const updated = [...prev];
        updated[index] = { ...updated[index], [field]: value } as PickupGroupMember;
        return updated;
      });
    },
    []
  );

  const handleMemberRemove = useCallback((index: number) => {
    setMembers(prev => prev.filter((_, i) => i !== index));
  }, []);

  const handleAddMember = useCallback(() => {
    const usedExtensions = new Set(members.map(member => member.extension));
    const unusedExtensions = availableExtensions.filter(ext => !usedExtensions.has(ext.id));

    if (unusedExtensions.length === 0) {
      showError(t('pickupGroups.messages.membersExhausted'));
      return;
    }

    const autoSelected = unusedExtensions.length === 1 ? unusedExtensions[0].id : '';

    setMembers(prev => ([
      ...prev,
      {
        extension: autoSelected,
        isMember: true,
        allowPickup: true
      }
    ]));
  }, [availableExtensions, members, showError, t]);

  const membersTable = useMemo(() => {
    return members.map((member, index) => {
      const usedExtensions = members.map(m => m.extension);
      const availableForSelect = availableExtensions.filter(ext => !usedExtensions.includes(ext.id) || ext.id === member.extension);

      return { member, index, availableForSelect };
    });
  }, [availableExtensions, members]);

  const handleDeleteConfirm = useCallback(async () => {
    setIsDeleting(true);
    try {
      await deletePickupGroupById(id as string);

      showSuccess(t('pickupGroups.messages.deleteSuccess'));
      navigate('/pbx/applications/pickup-groups');
    } catch (error) {
      showError(error?.message || t('pickupGroups.messages.deleteError'));
      setShowDeleteModal(false);
    } finally {
      setIsDeleting(false);
    }
  }, [id, navigate, showSuccess, showError, t]);

  // Quick List loader for Pickup Groups
  const loadInitialGroups = useCallback(async () => {
    if (initialGroups.length > 0) return;

    setIsLoadingInitial(true);
    try {
      const result = await fetchPickupGroups({
        page: 1,
        limit: 100,
        sortBy: 'name',
        sortOrder: 'asc'
      });
      setInitialGroups(result.data);
      setQuickListGroups(result.data);
    } catch (error) {
      console.error('Error loading initial groups:', error);
      setInitialGroups([]);
      setQuickListGroups([]);
    } finally {
      setIsLoadingInitial(false);
    }
  }, [initialGroups.length]);

  const loadQuickList = useCallback(async (search: string) => {
    const normalized = search.trim();

    if (!normalized || normalized.length < 2) {
      setQuickListGroups(initialGroups);
      return;
    }

    setIsQuickSearching(true);
    try {
      const result = await fetchPickupGroups({
        page: 1,
        limit: 50,
        sortBy: 'name',
        sortOrder: 'asc',
        search: normalized
      });
      setQuickListGroups(result.data);
    } catch (error) {
      console.error('Error loading quick list groups:', error);
      setQuickListGroups([]);
    } finally {
      setIsQuickSearching(false);
    }
  }, [initialGroups]);

  // Load initial groups when Quick List modal opens
  useEffect(() => {
    if (showQuickList && initialGroups.length === 0) {
      loadInitialGroups();
    }
  }, [showQuickList, initialGroups.length, loadInitialGroups]);

  // Debounced Quick List search
  useEffect(() => {
    if (!showQuickList) return;

    const timer = setTimeout(() => {
      if (quickListSearch.trim().length >= 2) {
        loadQuickList(quickListSearch);
      } else {
        // Restore initial groups if search is cleared
        setQuickListGroups(initialGroups);
      }
    }, 800);

    return () => clearTimeout(timer);
  }, [quickListSearch, showQuickList, loadQuickList, initialGroups]);

  const handleDuplicateFromForm = () => {
    if (!isEditMode || !id) return;
    navigateToPickupGroupDuplicate(navigate, id);
  };

  if (isLoading) {
    return (
      <div className="flex items-center justify-center min-h-screen">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary mx-auto mb-4" />
          <p className="text-muted-foreground">{t('common.loading')}</p>
        </div>
      </div>
    );
  }

  return (
    <div className="p-6 pb-20">
      <ModuleFormHeader
        icon={<PhoneOff className="h-5 w-5 text-primary" />}
        title={isEditMode ? `${t('pickupGroups.editTitle')} - ${formData.name || ''}` : t('pickupGroups.createTitle')}
        backButtonText={t('common.list')}
        onBack={() => navigate('/pbx/applications/pickup-groups')}
        actions={[
          {
            icon: <Search className="h-5 w-5 text-muted-foreground" />,
            tooltip: t('common.quickSearch'),
            onClick: () => setShowQuickList(true)
          },
          ...(isEditMode
            ? [{
              icon: <Copy className="h-5 w-5 text-muted-foreground" />,
              tooltip: t('common.duplicate'),
              onClick: handleDuplicateFromForm
            }]
            : []),
          {
            icon: <Plus className="h-5 w-5 text-muted-foreground" />,
            tooltip: t('pickupGroups.actions.add'),
            onClick: () => navigate('/pbx/applications/pickup-groups/new')
          }
        ]}
      />

      <div className="mt-8 space-y-6">
        <PickupGroupGeneralBox
          formData={formData}
          errors={errors}
          onChange={handleChange}
          onSettingChange={handleSettingChange}
          onSubmit={handleSaveForm}
          t={t}
        />
        <PickupGroupMembersBox
          membersTable={membersTable}
          errors={errors}
          onMemberChange={handleMemberChange}
          onMemberRemove={handleMemberRemove}
          onAddMember={handleAddMember}
          t={t}
        />
      </div>

      <FixedActionBar
        onSaveWithAction={handleSaveWithAction}
        onCancel={handleCancelForm}
        onDelete={isEditMode ? () => setShowDeleteModal(true) : undefined}
        isEditMode={isEditMode}
        isSaving={isSaving}
        deleteDisabled={isDeleting}
        cancelText={t('common.cancel')}
        deleteText={t('common.delete')}
        useSaveDropdown={true}
        saveDropdownStorageKey="pickupGroups:saveAction"
        saveDisabled={isSaving}
      />

      <ConfirmationModal
        isOpen={showDeleteModal}
        onClose={() => setShowDeleteModal(false)}
        onConfirm={handleDeleteConfirm}
        title={t('pickupGroups.deleteTitle')}
        message={t('pickupGroups.messages.confirmDelete', { name: formData.name })}
        confirmText={t('common.delete')}
        cancelText={t('common.cancel')}
        type="danger"
        isLoading={isDeleting}
      />

      {/* Quick List Modal for Pickup Groups */}
      <QuickSearchModal
        isOpen={showQuickList}
        onClose={() => {
          setShowQuickList(false);
          setQuickListSearch('');
        }}
        title={t('pickupGroups.title')}
        items={quickListGroups}
        isLoadingInitial={isLoadingInitial}
        isSearching={isQuickSearching}
        searchValue={quickListSearch}
        onSearchChange={setQuickListSearch}
        onItemSelect={(group) => {
          setShowQuickList(false);
          setQuickListSearch('');
          navigate(`/pbx/applications/pickup-groups/${group.uuid || group.id}`);
        }}
        currentItemId={id}
        renderItem={(group, isCurrent) => (
          <button
            onClick={() => {
              if (!isCurrent) {
                setShowQuickList(false);
                setQuickListSearch('');
                navigate(`/pbx/applications/pickup-groups/${group.uuid || group.id}`);
              }
            }}
            disabled={isCurrent}
            className={`w-full text-left p-3 rounded-md transition-colors ${isCurrent
              ? 'bg-primary/20 border-2 border-primary cursor-default'
              : 'hover:bg-gray-100 dark:hover:bg-gray-700 border border-border'
              }`}
          >
            <div className="flex items-center gap-3">
              <PhoneOff className="h-4 w-4 text-primary flex-shrink-0" />
              <div className="flex-1 min-w-0">
                <div className="flex items-center gap-2">
                  <span className="font-semibold text-foreground">
                    {group.name}
                  </span>
                  {isCurrent && (
                    <span className="px-2 py-0.5 text-xs font-medium bg-primary text-primary-foreground rounded">
                      {t('pickupGroups.current', 'Current')}
                    </span>
                  )}
                </div>
                {group.membersCount != null && (
                  <div className="text-sm text-muted-foreground truncate">
                    {t('pickupGroups.columns.members')}: {group.membersCount}
                  </div>
                )}
              </div>
            </div>
          </button>
        )}
        searchPlaceholder={t('common.searchPlaceholder')}
        minCharsMessage={t('pickupGroups.quickListMinChars', 'Type at least 2 characters to search')}
        emptyMessage={t('pickupGroups.quickListEmpty', 'Start typing to search pickup groups')}
        noResultsMessage={t('pickupGroups.quickListNoResults', 'No pickup groups found')}
        headerIcon={<PhoneOff className="h-5 w-5" />}
        minSearchChars={2}
      />
    </div>
  );
}

